import { type ReactElement, useEffect, useRef, useState } from 'react';
import type { WorkspaceContract } from '@nest-react/domain';
import { LockKeyhole } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { apiRequest, useWorkspace } from '~/app/providers';
import { ACTIVE_REGION_NAME_I18N_KEY } from '~/app/regionLabel';
import { Button } from '~/components/ui/button';

const INDUSTRIES = ['ecommerce', 'agency', 'gaming', 'brand', 'other'] as const;
const COMPANY_SIZES = ['small', 'medium', 'large', 'enterprise'] as const;

export const WorkspaceSettingsPage = (): ReactElement => {
  const { t } = useTranslation('account');
  const { t: to } = useTranslation('onboarding');
  const { activeWorkspace, refreshWorkspaces } = useWorkspace();
  const [form, setForm] = useState(() => workspaceForm(activeWorkspace));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const generation = useRef(0);

  // The active workspace is an external selection; changing it starts a fresh form session.

  useEffect(() => {
    generation.current += 1;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setForm(workspaceForm(activeWorkspace));
    setSaving(false);
    setMessage('');
    setError('');
  }, [activeWorkspace?.id]);

  const save = async (): Promise<void> => {
    if (
      !activeWorkspace ||
      (activeWorkspace.role !== 'OWNER' && activeWorkspace.role !== 'ADMIN')
    )
      return;
    const requestGeneration = generation.current;
    const workspaceId = activeWorkspace.id;
    const name = form.name.trim();
    if (!name) {
      setError(t('workspaceNameRequired'));
      return;
    }
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await apiRequest(`/workspaces/${workspaceId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name,
          website: form.website.trim(),
          industryCode: form.industryCode,
          companySizeCode: form.companySizeCode,
        }),
      });
      if (generation.current !== requestGeneration) return;
      await refreshWorkspaces();
      if (generation.current !== requestGeneration) return;
      setMessage(t('workspaceUpdated'));
    } catch {
      if (generation.current !== requestGeneration) return;
      setError(t('workspaceUpdateFailed'));
    } finally {
      if (generation.current === requestGeneration) setSaving(false);
    }
  };

  if (!activeWorkspace)
    return <p className="text-sm text-muted">{t('noWorkspaces')}</p>;

  const editable =
    activeWorkspace.role === 'OWNER' || activeWorkspace.role === 'ADMIN';

  return (
    <section className="overflow-hidden rounded-3xl border border-border bg-elevated shadow-sm">
      <div className="flex flex-col gap-4 border-b border-border-subtle bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
        <div className="min-w-0">
          <h2 className="truncate text-2xl font-semibold text-foreground">
            {activeWorkspace.name}
          </h2>
          <p className="mt-1 text-sm text-muted">
            {activeWorkspace.role} · {t(ACTIVE_REGION_NAME_I18N_KEY)}
          </p>
        </div>
        <span className="w-fit rounded-full border border-border bg-background px-3 py-1.5 font-mono text-xs text-muted">
          {activeWorkspace.slug}
        </span>
      </div>

      <div className="p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {t('workspaceDetails')}
            </h2>
            <p className="mt-1 text-sm text-muted">
              {t('workspaceDetailsHint')}
            </p>
          </div>
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <Field label={t('workspaceName')}>
            <input
              aria-label={t('workspaceName')}
              autoComplete="organization"
              className={fieldClass}
              disabled={!editable}
              name="workspaceName"
              onChange={event =>
                setForm(current => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              value={form.name}
            />
          </Field>
          <Field label={to('website')}>
            <input
              aria-label={to('website')}
              autoComplete="url"
              className={fieldClass}
              disabled={!editable}
              name="website"
              onChange={event =>
                setForm(current => ({
                  ...current,
                  website: event.target.value,
                }))
              }
              type="url"
              value={form.website}
            />
          </Field>
          <Field label={to('industry')}>
            <select
              aria-label={to('industry')}
              className={fieldClass}
              disabled={!editable}
              name="industryCode"
              onChange={event =>
                setForm(current => ({
                  ...current,
                  industryCode: event.target.value,
                }))
              }
              value={form.industryCode}
            >
              {INDUSTRIES.map(value => (
                <option key={value} value={value}>
                  {to(`industries.${value}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={to('size')}>
            <select
              aria-label={to('size')}
              className={fieldClass}
              disabled={!editable}
              name="companySizeCode"
              onChange={event =>
                setForm(current => ({
                  ...current,
                  companySizeCode: event.target.value,
                }))
              }
              value={form.companySizeCode}
            >
              {COMPANY_SIZES.map(value => (
                <option key={value} value={value}>
                  {to(`sizes.${value}`)}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {!editable ? (
          <div className="mt-6 flex items-center gap-2 rounded-2xl border border-border-subtle bg-surface p-4 text-sm text-muted">
            <LockKeyhole aria-hidden className="size-4 shrink-0" />
            {t('workspaceReadOnly')}
          </div>
        ) : null}
        {error ? (
          <p className="mt-5 text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
        {message ? (
          <p className="mt-5 text-sm text-emerald-600" role="status">
            {message}
          </p>
        ) : null}
        {editable ? (
          <Button
            className="mt-6 min-h-11 rounded-xl px-5"
            disabled={saving}
            onClick={() => void save()}
            type="button"
          >
            {saving ? t('saving') : t('saveChanges')}
          </Button>
        ) : null}
      </div>
    </section>
  );
};

const workspaceForm = (
  workspace?: WorkspaceContract | null
): {
  name: string;
  website: string;
  industryCode: string;
  companySizeCode: string;
} => ({
  name: workspace?.name ?? '',
  website: workspace?.profile.website ?? '',
  industryCode: workspace?.profile.industryCode ?? 'other',
  companySizeCode: workspace?.profile.companySizeCode ?? 'small',
});

const fieldClass =
  'min-h-11 w-full rounded-xl border border-border bg-background px-3.5 text-foreground outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:bg-surface disabled:text-muted';

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): ReactElement => (
  <label className="grid gap-2 text-sm font-medium text-foreground">
    {label}
    {children}
  </label>
);
